/**
 * 学習計画詳細ページ
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Cancel as CancelIcon,
  CheckCircle as ApproveIcon,
  DoNotDisturb as RejectIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { LearningPlanService } from '../services/learningPlanService'
import {
  LearningPlanResponse,
  ScheduledTestResponse,
  LearningPlanProgressResponse,
  PLAN_STATUS_COLORS,
  SCHEDULED_TEST_STATUS_COLORS,
} from '../types/learningPlan'

const LearningPlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('learningPlan')
  const [plan, setPlan] = useState<LearningPlanResponse | null>(null)
  const [scheduledTests, setScheduledTests] = useState<ScheduledTestResponse[]>([])
  const [progress, setProgress] = useState<LearningPlanProgressResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectTestId, setRejectTestId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  const loadData = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError('')
      const [planData, testsData, progressData] = await Promise.all([
        LearningPlanService.getPlan(id),
        LearningPlanService.getScheduledTests(id),
        LearningPlanService.getPlanProgress(id),
      ])
      setPlan(planData)
      setScheduledTests(testsData)
      setProgress(progressData)
    } catch (err) {
      console.error('データ読み込みエラー:', err)
      setError(t('messages.loadDetailError'))
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleActivate = async () => {
    if (!id) return
    try {
      setActionLoading(true)
      await LearningPlanService.activatePlan(id)
      setSuccessMessage(t('messages.activateSuccess'))
      loadData()
    } catch (err) {
      console.error('有効化エラー:', err)
      setError(t('messages.activateError'))
    } finally {
      setActionLoading(false)
    }
  }

  const handlePause = async () => {
    if (!id) return
    try {
      setActionLoading(true)
      await LearningPlanService.pausePlan(id)
      setSuccessMessage(t('messages.pauseSuccess'))
      loadData()
    } catch (err) {
      console.error('一時停止エラー:', err)
      setError(t('messages.pauseError'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!id) return
    if (!window.confirm(t('messages.cancelConfirm'))) return
    try {
      setActionLoading(true)
      await LearningPlanService.cancelPlan(id)
      setSuccessMessage(t('messages.cancelSuccess'))
      loadData()
    } catch (err) {
      console.error('キャンセルエラー:', err)
      setError(t('messages.cancelError'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleApproveTest = async (testId: string) => {
    try {
      setActionLoading(true)
      await LearningPlanService.approveScheduledTest(testId)
      setSuccessMessage(t('messages.approveSuccess'))
      loadData()
    } catch (err) {
      console.error('承認エラー:', err)
      setError(t('messages.approveError'))
    } finally {
      setActionLoading(false)
    }
  }

  const handleRejectTest = async () => {
    if (!rejectTestId || !rejectReason.trim()) return
    try {
      setActionLoading(true)
      await LearningPlanService.rejectScheduledTest(rejectTestId, rejectReason)
      setSuccessMessage(t('messages.rejectSuccess'))
      setRejectDialogOpen(false)
      setRejectTestId(null)
      setRejectReason('')
      loadData()
    } catch (err) {
      console.error('却下エラー:', err)
      setError(t('messages.rejectError'))
    } finally {
      setActionLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('detail.notFound')}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/learning-plans')} sx={{ mt: 2 }}>
          {t('detail.backToList')}
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        {/* ヘッダー */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate('/learning-plans')}
              sx={{ mb: 1 }}
            >
              {t('detail.backToList')}
            </Button>
            <Typography variant="h4" component="h1">
              {plan.title}
            </Typography>
            {plan.description && (
              <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                {plan.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={loadData} disabled={actionLoading}>
              <RefreshIcon />
            </IconButton>
            {(plan.status === 'DRAFT' || plan.status === 'PAUSED') && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayIcon />}
                onClick={handleActivate}
                disabled={actionLoading}
              >
                {t('actions.activate')}
              </Button>
            )}
            {plan.status === 'ACTIVE' && (
              <Button
                variant="outlined"
                startIcon={<PauseIcon />}
                onClick={handlePause}
                disabled={actionLoading}
              >
                {t('actions.pause')}
              </Button>
            )}
            {(plan.status === 'DRAFT' || plan.status === 'ACTIVE' || plan.status === 'PAUSED') && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={actionLoading}
              >
                {t('actions.cancel')}
              </Button>
            )}
          </Stack>
        </Box>

        {/* メッセージ */}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {/* 基本情報 */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 2 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('detail.progressSection')}
              </Typography>
              {progress && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {t('detail.completedTests', {
                        completed: progress.completedTests,
                        total: progress.totalScheduledTests,
                      })}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {progress.overallProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress.overallProgress}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
              )}
            </Paper>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.statusLabel')}
                  </Typography>
                  <Chip
                    label={t(`status.${plan.status}`)}
                    color={PLAN_STATUS_COLORS[plan.status]}
                    size="small"
                  />
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.complianceRule')}
                  </Typography>
                  <Typography variant="body2">{plan.complianceRule?.title}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.targetGroup')}
                  </Typography>
                  <Typography variant="body2">
                    {plan.userGroup?.memberCount !== undefined
                      ? t('detail.targetGroupWithCount', {
                          name: plan.userGroup?.name,
                          count: plan.userGroup.memberCount,
                        })
                      : plan.userGroup?.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.frequencyLabel')}
                  </Typography>
                  <Typography variant="body2">{t(`frequency.${plan.frequency}`)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.periodLabel')}
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(plan.startDate)} 〜 {formatDate(plan.endDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.difficultyLabel')}
                  </Typography>
                  <Typography variant="body2">{t(`difficulty.${plan.difficulty}`)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.passingScore')}
                  </Typography>
                  <Typography variant="body2">
                    {t('detail.passingScoreValue', { score: plan.passingScore })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.questionCount')}
                  </Typography>
                  <Typography variant="body2">
                    {t('detail.questionCountValue', { count: plan.questionCount })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('detail.requireApproval')}
                  </Typography>
                  <Typography variant="body2">
                    {plan.requireApproval ? t('detail.yes') : t('detail.no')}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Box>

        {/* スケジュール済みテスト */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('scheduledTests.title')}
          </Typography>
          {scheduledTests.length === 0 ? (
            <Typography color="textSecondary">{t('scheduledTests.noTests')}</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>{t('scheduledTests.columns.sequence')}</TableCell>
                    <TableCell>{t('scheduledTests.columns.scheduledDate')}</TableCell>
                    <TableCell>{t('scheduledTests.columns.generationDate')}</TableCell>
                    <TableCell>{t('scheduledTests.columns.dueDate')}</TableCell>
                    <TableCell>{t('scheduledTests.columns.status')}</TableCell>
                    <TableCell>{t('scheduledTests.columns.progress')}</TableCell>
                    <TableCell align="right">{t('scheduledTests.columns.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduledTests.map((test) => (
                    <TableRow key={test.id} hover>
                      <TableCell>
                        {t('scheduledTests.columns.sequenceNumber', {
                          number: test.sequenceNumber,
                        })}
                      </TableCell>
                      <TableCell>{formatDate(test.scheduledDate)}</TableCell>
                      <TableCell>{formatDate(test.generationDate)}</TableCell>
                      <TableCell>{formatDate(test.dueDate)}</TableCell>
                      <TableCell>
                        <Chip
                          label={t(`scheduledTestStatus.${test.status}`)}
                          color={SCHEDULED_TEST_STATUS_COLORS[test.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {test.progress && (
                          <Box>
                            <Typography variant="caption">
                              {t('scheduledTests.completedCount', {
                                completed: test.progress.completedCount,
                                total: test.progress.totalParticipants,
                              })}
                            </Typography>
                            {test.progress.averageScore !== null && (
                              <Typography variant="caption" display="block">
                                {t('scheduledTests.averageScore', {
                                  score: test.progress.averageScore.toFixed(1),
                                })}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {test.status === 'GENERATED' && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title={t('scheduledTests.approve')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleApproveTest(test.id)}
                                disabled={actionLoading}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('scheduledTests.reject')}>
                              <IconButton
                                size="small"
                                sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}
                                onClick={() => {
                                  setRejectTestId(test.id)
                                  setRejectDialogOpen(true)
                                }}
                                disabled={actionLoading}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* 参加者統計 */}
        {progress && progress.participantStats.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('participantProgress.title')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>{t('participantProgress.columns.participant')}</TableCell>
                    <TableCell>{t('participantProgress.columns.completedTests')}</TableCell>
                    <TableCell>{t('participantProgress.columns.averageScore')}</TableCell>
                    <TableCell>{t('participantProgress.columns.lastTestDate')}</TableCell>
                    <TableCell>{t('participantProgress.columns.trend')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {progress.participantStats.map((stat) => (
                    <TableRow key={stat.userId} hover>
                      <TableCell>{stat.userName}</TableCell>
                      <TableCell>
                        {stat.testsCompleted} / {stat.testsTotal}
                      </TableCell>
                      <TableCell>
                        {stat.averageScore !== null
                          ? t('participantProgress.scoreValue', {
                              score: stat.averageScore.toFixed(1),
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {stat.lastTestDate ? formatDate(stat.lastTestDate) : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(`participantProgress.trend.${stat.trend}`)}
                          color={
                            stat.trend === 'improving'
                              ? 'success'
                              : stat.trend === 'stable'
                                ? 'info'
                                : stat.trend === 'declining'
                                  ? 'warning'
                                  : 'default'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>

      {/* 却下ダイアログ */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false)
          setRejectTestId(null)
          setRejectReason('')
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('rejectDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('rejectDialog.description')}
          </Typography>
          <TextField
            label={t('rejectDialog.reasonLabel')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false)
              setRejectTestId(null)
              setRejectReason('')
            }}
          >
            {t('rejectDialog.cancel')}
          </Button>
          <Button
            onClick={handleRejectTest}
            variant="outlined"
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              '&:hover': { borderColor: '#EF4444', color: '#EF4444' },
            }}
            disabled={!rejectReason.trim() || actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : t('rejectDialog.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default LearningPlanDetailPage
